import React, { useState } from "react";
import { BedDouble, Heart } from "lucide-react";
import { inr } from "@/lib/format";
import { Card } from "@/components/common";
import { photoSrc } from "@/components/Photos";
import { RoomAmenityChips } from "@/components/RoomAmenities";
import { CompareBar, RoomCompareSheet, useCompare } from "@/components/RoomCompare";
import { useShortlist } from "@/pages/resident/useShortlist";

export function SaveHeart({ saved, onClick, testId }: { saved: boolean; onClick: () => void; testId: string }) {
  return (
    <button data-testid={testId} aria-label={saved ? "Remove from saved" : "Save room"} aria-pressed={saved} onClick={onClick}
      className={`h-11 w-11 shrink-0 rounded-xl border grid place-items-center pg-tap ${saved ? "border-rose-200 bg-rose-50 text-rose-600" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
      <Heart className={`h-5 w-5 ${saved ? "fill-current" : ""}`} />
    </button>
  );
}

function RoomCard({ r, onRequest, picked, onPick, saved, onSave }: { r: any; onRequest?: (r: any) => void; picked: boolean; onPick: () => void; saved: boolean; onSave: () => void }) {
  return (
    <Card className={`p-0 overflow-hidden ${picked ? "ring-2 ring-[#131e2e]" : ""}`} data-testid={`listing-room-${r.name}`}>
      {r.photos.length > 0 && (
        <div className="flex gap-1 overflow-x-auto snap-x snap-mandatory">
          {r.photos.map((p) => (
            <img key={p.id} src={photoSrc(p.url)} alt={p.caption || `Room ${r.name}`} loading="lazy"
              className={`snap-start shrink-0 aspect-[4/3] object-cover bg-slate-100 ${r.photos.length > 1 ? "w-[80%]" : "w-full"}`} />
          ))}
        </div>
      )}
      <div className="p-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold">Room {r.name}{r.floor ? <span className="text-xs font-semibold text-slate-500"> · Floor {r.floor}</span> : null}</p>
          <p className="text-xs text-slate-500">{r.sharing === 1 ? "Single room" : `${r.sharing}-sharing`} · Deposit {inr(r.deposit)}</p>
          <p className={`mt-1 text-xs font-semibold flex items-center gap-1 ${r.vacant_beds ? "text-green-700" : "text-slate-400"}`} data-testid={`listing-room-free-${r.name}`}>
            <BedDouble className="h-3.5 w-3.5" />{r.vacant_beds ? `${r.vacant_beds} of ${r.sharing} beds free` : "Full"}
          </p>
        </div>
        <p className="text-right shrink-0"><span className="text-lg font-extrabold font-mono-num text-[#131e2e]">{inr(r.rent_per_bed)}</span><br /><span className="text-[11px] text-slate-500">per bed / month</span></p>
      </div>
      <div className="px-4 pb-4 -mt-1 space-y-3">
        <RoomAmenityChips items={r.amenities} testId={`listing-room-amenities-${r.name}`} />
        <div className="flex gap-2">
          <SaveHeart saved={saved} onClick={onSave} testId={`listing-room-save-${r.name}`} />
          <label data-testid={`listing-room-compare-${r.name}`} className={`h-11 px-3 rounded-xl border text-sm font-semibold flex items-center gap-2 cursor-pointer select-none pg-tap ${picked ? "border-[#131e2e] bg-[#e8f1fb] text-[#131e2e]" : "border-slate-200 text-slate-600"}`}>
            <input type="checkbox" checked={picked} onChange={onPick} className="h-4 w-4 accent-[#131e2e]" />Compare
          </label>
          {onRequest && r.vacant_beds > 0 && (
            <button data-testid={`listing-room-request-${r.name}`} onClick={() => onRequest(r)}
              className="flex-1 h-11 rounded-xl border-2 border-[#131e2e] text-[#131e2e] font-semibold text-sm pg-tap hover:bg-[#e8f1fb]">Request this room</button>
          )}
        </div>
      </div>
    </Card>
  );
}

export function ListingRooms({ rooms, onRequest }: { rooms: any[]; onRequest?: (r: any) => void }) {
  const { picked, toggle, clear } = useCompare();
  const shortlist = useShortlist();
  const [open, setOpen] = useState(false);
  if (!rooms?.length) return null;
  const sel = rooms.filter((r) => picked.includes(r.id));
  return (
    <div className="space-y-2.5" data-testid="listing-rooms">
      <div className="flex items-baseline justify-between">
        <p className="font-bold">Rooms</p>
        {rooms.length > 1 && <p className="text-xs text-slate-500">Tick 2–3 rooms to compare</p>}
      </div>
      {rooms.map((r) => <RoomCard key={r.id} r={r} onRequest={onRequest} picked={picked.includes(r.id)} onPick={() => toggle(r.id)} saved={shortlist.ids.has(r.id)} onSave={() => shortlist.toggle(r.id)} />)}
      <CompareBar count={picked.length} onClear={clear} onOpen={() => setOpen(true)} />
      {open && <RoomCompareSheet rooms={sel} open={open} onClose={() => setOpen(false)} onRequest={onRequest} />}
    </div>
  );
}
